import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Sparkles, Image, Type, Palette, Download, Copy, 
  Loader2, Wand2, RefreshCw, Check, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const logoSessionKey = (websiteId) => `ai-logo-sessions:${websiteId || 'global'}`;

const loadLogoSessions = (websiteId) => {
  try {
    const raw = localStorage.getItem(logoSessionKey(websiteId));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const persistLogoSessions = (websiteId, sessions) => {
  try {
    localStorage.setItem(logoSessionKey(websiteId), JSON.stringify(sessions.slice(0, 24)));
  } catch { /* quota */ }
};

const AITools = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('logo');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [logoSessions, setLogoSessions] = useState([]);
  const [applying, setApplying] = useState(false);

  // Logo Generator State
  const [logoPrompt, setLogoPrompt] = useState('');
  const [logoStyle, setLogoStyle] = useState('modern');
  const [logoColors, setLogoColors] = useState(['#8b5cf6', '#10b981']);

  // Text Generator State
  const [textPrompt, setTextPrompt] = useState('');
  const [textType, setTextType] = useState('headline');
  const [textTone, setTextTone] = useState('professional');

  // Image Generator State
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageStyle, setImageStyle] = useState('realistic');
  const [imageSize, setImageSize] = useState('1024x1024');

  const token = localStorage.getItem('token');

  useEffect(() => {
    const local = loadLogoSessions(websiteId);
    setLogoSessions(local);
    if (!websiteId || !token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/websites/${websiteId}/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const remote = Array.isArray(data.logo_history) ? data.logo_history : [];
        if (!remote.length) return;
        const merged = [];
        const seen = new Set();
        for (const item of [...remote, ...local]) {
          const url = item?.url || item?.logo_url;
          if (!url || seen.has(url)) continue;
          seen.add(url);
          merged.push({
            id: item.id || url,
            type: 'logo',
            url,
            prompt: item.prompt || '',
            style: item.style || '',
            created_at: item.created_at || '',
          });
        }
        const next = merged.slice(0, 24);
        setLogoSessions(next);
        persistLogoSessions(websiteId, next);
      } catch { /* ignore */ }
    })();
  }, [websiteId, token]);

  const pushLogoSession = useCallback((entry) => {
    setLogoSessions((prev) => {
      const next = [entry, ...prev.filter((s) => s.url !== entry.url)].slice(0, 24);
      persistLogoSessions(websiteId, next);
      return next;
    });
  }, [websiteId]);

  const handleGenerateLogo = async () => {
    if (!logoPrompt.trim()) {
      toast.error('Please enter a description for your logo');
      return;
    }
    setLoading(true);
    setResult(null);
    
    try {
      const response = await fetch(`${API}/api/ai/generate-logo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: logoPrompt,
          style: logoStyle,
          colors: logoColors,
          website_id: websiteId
        })
      });

      const data = await response.json().catch(() => ({}));
      
      if (response.ok && data.logo_url) {
        const entry = {
          id: data.session_id || `${Date.now()}`,
          type: 'logo',
          url: data.logo_url,
          prompt: logoPrompt,
          style: logoStyle,
          created_at: new Date().toISOString(),
        };
        setResult(entry);
        pushLogoSession(entry);
        toast.success('Logo generated successfully!');
      } else {
        const detail = typeof data.detail === 'string' ? data.detail : 'Logo generation failed';
        toast.error(detail);
      }
    } catch (error) {
      toast.error(error.message || 'Logo generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateText = async () => {
    if (!textPrompt.trim()) {
      toast.error('Please enter a description for your text');
      return;
    }
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: textPrompt,
          type: textType,
          tone: textTone,
          website_id: websiteId
        })
      });

      const data = await response.json();
      
      if (response.ok && data.text) {
        setResult({ type: 'text', content: data.text, prompt: textPrompt });
        toast.success('Text generated successfully!');
      } else {
        // Mock response for demo
        const mockTexts = {
          headline: `Transform Your ${textPrompt} Experience`,
          tagline: `${textPrompt} - Where Excellence Meets Innovation`,
          description: `Discover the ultimate solution for ${textPrompt}. Our innovative approach combines cutting-edge technology with user-centric design to deliver exceptional results. Whether you're just getting started or scaling up, we have the perfect solution for you.`,
          cta: `Get Started with ${textPrompt} Today!`,
          about: `We are passionate about ${textPrompt}. Our team of dedicated experts has been helping businesses succeed for over a decade. With a commitment to excellence and innovation, we deliver solutions that make a real difference.`
        };
        setResult({ 
          type: 'text', 
          content: mockTexts[textType] || mockTexts.headline,
          prompt: textPrompt,
          isMock: true
        });
        toast.info('Demo: Text generation ready for AI integration');
      }
    } catch (error) {
      const mockTexts = {
        headline: `Transform Your ${textPrompt} Experience`,
        tagline: `${textPrompt} - Where Excellence Meets Innovation`,
        description: `Discover the ultimate solution for ${textPrompt}. Our innovative approach combines cutting-edge technology with user-centric design to deliver exceptional results.`,
        cta: `Get Started with ${textPrompt} Today!`,
        about: `We are passionate about ${textPrompt}. Our team of dedicated experts delivers solutions that make a real difference.`
      };
      setResult({ 
        type: 'text', 
        content: mockTexts[textType] || mockTexts.headline,
        prompt: textPrompt,
        isMock: true
      });
      toast.info('Demo: Text generation ready for AI integration');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) {
      toast.error('Please enter a description for your image');
      return;
    }
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: imagePrompt,
          style: imageStyle,
          size: imageSize,
          website_id: websiteId
        })
      });

      const data = await response.json();
      
      if (response.ok && data.image_url) {
        setResult({ type: 'image', url: data.image_url, prompt: imagePrompt });
        toast.success('Image generated successfully!');
      } else {
        // Mock response with Unsplash
        const unsplashQuery = encodeURIComponent(imagePrompt);
        setResult({ 
          type: 'image', 
          url: `https://source.unsplash.com/1024x1024/?${unsplashQuery}`,
          prompt: imagePrompt,
          isMock: true
        });
        toast.info('Demo: Image generation ready for AI integration');
      }
    } catch (error) {
      const unsplashQuery = encodeURIComponent(imagePrompt);
      setResult({ 
        type: 'image', 
        url: `https://source.unsplash.com/1024x1024/?${unsplashQuery}`,
        prompt: imagePrompt,
        isMock: true
      });
      toast.info('Demo: Image generation ready for AI integration');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadImage = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'generated-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started!');
  };

  const useInWebsite = async (override = null) => {
    const item = override || result;
    if (!item) return;

    if (item.type === 'logo') {
      if (!websiteId) {
        toast.error('Open AI Tools from a website editor to apply the logo');
        return;
      }
      setApplying(true);
      try {
        const response = await fetch(`${API}/api/websites/${websiteId}/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          // Header logo uses logo_url (not favicon)
          body: JSON.stringify({ logo_url: item.url })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to apply logo');
        }
        toast.success('Logo applied to website header');
        navigate(`/edit/${websiteId}`);
      } catch (error) {
        toast.error(error.message || 'Failed to apply logo');
      } finally {
        setApplying(false);
      }
    } else if (item.type === 'text') {
      copyToClipboard(item.content);
      toast.success('Text copied! Paste it in the editor.');
    } else if (item.type === 'image') {
      copyToClipboard(item.url);
      toast.success('Image URL copied! Use it in the editor.');
    }
  };

  const tabs = [
    { id: 'logo', name: 'Logo Generator', icon: Palette },
    { id: 'text', name: 'Text Generator', icon: Type },
    { id: 'image', name: 'Image Generator', icon: Image }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/edit/${websiteId}`)}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Editor
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-violet-400" />
              <span className="text-xl font-bold">AI Creative Tools</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setResult(null); }}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-violet-500 text-white'
                  : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-white'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
            {activeTab === 'logo' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Generate Logo</h2>
                  <p className="text-zinc-400">Create a unique logo for your brand using AI</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Brand/Business Name</label>
                  <Input
                    value={logoPrompt}
                    onChange={(e) => setLogoPrompt(e.target.value)}
                    placeholder="e.g., TechFlow, GreenLeaf, SparkHub"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Style</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['modern', 'minimal', 'playful', 'corporate', 'vintage', 'tech'].map((style) => (
                      <button
                        key={style}
                        onClick={() => setLogoStyle(style)}
                        className={`px-4 py-2 rounded-lg capitalize transition-all ${
                          logoStyle === style
                            ? 'bg-violet-500 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Primary Colors</label>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={logoColors[0]}
                        onChange={(e) => setLogoColors([e.target.value, logoColors[1]])}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <span className="text-sm text-zinc-400">Primary</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={logoColors[1]}
                        onChange={(e) => setLogoColors([logoColors[0], e.target.value])}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <span className="text-sm text-zinc-400">Secondary</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleGenerateLogo}
                  disabled={loading || !logoPrompt.trim()}
                  className="w-full bg-violet-500 hover:bg-violet-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Logo
                    </>
                  )}
                </Button>

                {logoSessions.length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-zinc-800" data-testid="ai-logo-sessions">
                    <p className="text-sm font-medium text-zinc-300">Previous logos</p>
                    <p className="text-xs text-zinc-500">Saved on this device for this website</p>
                    <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto">
                      {logoSessions.map((s) => (
                        <button
                          key={s.id || s.url}
                          type="button"
                          onClick={() => setResult({ ...s, type: 'logo' })}
                          className={`rounded-lg border overflow-hidden bg-zinc-950 text-left ${
                            result?.url === s.url ? 'border-violet-400' : 'border-zinc-700 hover:border-zinc-500'
                          }`}
                          title={s.prompt || 'Logo'}
                        >
                          <img src={s.url} alt="" className="w-full aspect-square object-contain bg-zinc-900" />
                          <span className="block px-1.5 py-1 text-[10px] text-zinc-500 truncate">{s.prompt || 'Logo'}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'text' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Generate Text</h2>
                  <p className="text-zinc-400">Create compelling copy for your website</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">What&apos;s the text about?</label>
                  <Input
                    value={textPrompt}
                    onChange={(e) => setTextPrompt(e.target.value)}
                    placeholder="e.g., coffee shop, tech startup, fitness app"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Text Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'headline', label: 'Headline' },
                      { id: 'tagline', label: 'Tagline' },
                      { id: 'description', label: 'Description' },
                      { id: 'cta', label: 'Call to Action' },
                      { id: 'about', label: 'About Us' }
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setTextType(type.id)}
                        className={`px-4 py-2 rounded-lg transition-all ${
                          textType === type.id
                            ? 'bg-violet-500 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Tone</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['professional', 'casual', 'friendly', 'formal', 'witty', 'inspirational'].map((tone) => (
                      <button
                        key={tone}
                        onClick={() => setTextTone(tone)}
                        className={`px-4 py-2 rounded-lg capitalize transition-all ${
                          textTone === tone
                            ? 'bg-violet-500 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {tone}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleGenerateText}
                  disabled={loading || !textPrompt.trim()}
                  className="w-full bg-violet-500 hover:bg-violet-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Text
                    </>
                  )}
                </Button>
              </div>
            )}

            {activeTab === 'image' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Generate Image</h2>
                  <p className="text-zinc-400">Create custom images for your website</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Describe Your Image</label>
                  <textarea
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="e.g., modern office interior with plants, abstract gradient background, team working together"
                    className="w-full h-24 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Style</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['realistic', 'illustration', 'abstract', '3d', 'minimalist', 'artistic'].map((style) => (
                      <button
                        key={style}
                        onClick={() => setImageStyle(style)}
                        className={`px-4 py-2 rounded-lg capitalize transition-all ${
                          imageStyle === style
                            ? 'bg-violet-500 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Size</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['1024x1024', '1792x1024', '1024x1792'].map((size) => (
                      <button
                        key={size}
                        onClick={() => setImageSize(size)}
                        className={`px-4 py-2 rounded-lg transition-all ${
                          imageSize === size
                            ? 'bg-violet-500 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {size === '1024x1024' ? 'Square' : size === '1792x1024' ? 'Landscape' : 'Portrait'}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleGenerateImage}
                  disabled={loading || !imagePrompt.trim()}
                  className="w-full bg-violet-500 hover:bg-violet-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Image
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Result Section */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-4">Result</h3>
            
            {loading && (
              <div className="flex flex-col items-center justify-center h-80">
                <Loader2 className="w-12 h-12 text-violet-400 animate-spin mb-4" />
                <p className="text-zinc-400">Creating your {activeTab}...</p>
              </div>
            )}

            {!loading && !result && (
              <div className="flex flex-col items-center justify-center h-80 text-zinc-500">
                <Sparkles className="w-12 h-12 mb-4 opacity-50" />
                <p>Your generated {activeTab} will appear here</p>
              </div>
            )}

            {!loading && result && (
              <div className="space-y-4">
                {result.isMock && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    Demo mode: Connect AI to generate custom content
                  </div>
                )}

                {result.type === 'logo' && (
                  <div className="flex flex-col items-center">
                    <div className="bg-zinc-800 rounded-2xl p-8 mb-4">
                      <img 
                        src={result.url} 
                        alt="Generated Logo"
                        className="w-48 h-48 object-contain"
                      />
                    </div>
                    <p className="text-zinc-400 text-sm mb-4">&ldquo;{result.prompt}&rdquo;</p>
                  </div>
                )}

                {result.type === 'text' && (
                  <div className="bg-zinc-800 rounded-xl p-6">
                    <p className="text-lg leading-relaxed">{result.content}</p>
                    <p className="text-zinc-500 text-sm mt-4">Based on: &ldquo;{result.prompt}&rdquo;</p>
                  </div>
                )}

                {result.type === 'image' && (
                  <div className="flex flex-col items-center">
                    <img 
                      src={result.url} 
                      alt="Generated"
                      className="w-full max-h-80 object-cover rounded-xl mb-4"
                    />
                    <p className="text-zinc-400 text-sm">&ldquo;{result.prompt}&rdquo;</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => {
                      if (activeTab === 'logo') handleGenerateLogo();
                      else if (activeTab === 'text') handleGenerateText();
                      else handleGenerateImage();
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                  
                  {result.type === 'text' ? (
                    <Button
                      onClick={() => copyToClipboard(result.content)}
                      className="flex-1 bg-violet-500 hover:bg-violet-600"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Text
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => downloadImage(result.url, `${result.type}-${Date.now()}.png`)}
                        variant="outline"
                        className="flex-1"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        onClick={() => useInWebsite()}
                        disabled={applying || (result.type === 'logo' && !websiteId)}
                        className="flex-1 min-w-0 bg-violet-500 hover:bg-violet-600"
                        data-testid="ai-tools-use-in-website"
                      >
                        {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" /> : <Check className="w-4 h-4 mr-2 shrink-0" />}
                        <span className="truncate">Use in Website</span>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITools;
